import type {
	DTOOf,
	DTOOfTree,
	IWorkfrontBundleDetail,
	IWorkfrontBundleListItem,
	IWorkfrontBundleNode,
	IWorkfrontBundleVersion,
	IWorkfrontHealth,
	IWorkfrontOverview,
	IWorkfrontRun,
	IWorkfrontScopeEntry,
	IWorkfrontStepRun,
	IWorkfrontStreamStatus,
	IWorkfrontSyncError,
	PaginatedResponse,
	WorkfrontBundleState,
	WorkfrontBundleSyncStatus,
	WorkfrontProcess,
	WorkfrontRunStatus,
	WorkfrontScopeKey,
	WorkfrontSyncErrorSeverity,
	WorkfrontSyncErrorStatus,
	WorkfrontSyncErrorStep,
} from '@atlas/types'
import { apiClient } from './api.client'

/** The shapes as the API serializes them: dates are ISO strings. */
export type WorkfrontHealthDTO = DTOOf<IWorkfrontHealth>
export type WorkfrontStreamDTO = DTOOf<IWorkfrontStreamStatus>
export type WorkfrontRunDTO = DTOOf<IWorkfrontRun>
export type WorkfrontStepRunDTO = DTOOf<IWorkfrontStepRun>
export type WorkfrontRunDetailDTO = WorkfrontRunDTO & { steps: WorkfrontStepRunDTO[] }
export type WorkfrontErrorDTO = DTOOf<IWorkfrontSyncError>
export type WorkfrontScopeEntryDTO = DTOOf<IWorkfrontScopeEntry>
export type WorkfrontBundleDTO = DTOOf<IWorkfrontBundleListItem>
export type WorkfrontBundleVersionDTO = DTOOf<IWorkfrontBundleVersion>
export type WorkfrontBundleNodeDTO = DTOOfTree<IWorkfrontBundleNode>
export type WorkfrontBundleDetailDTO = DTOOf<IWorkfrontBundleDetail, 'tree' | 'versions' | 'errors'> & {
	tree: WorkfrontBundleNodeDTO[]
	versions: WorkfrontBundleVersionDTO[]
	errors: WorkfrontErrorDTO[]
}
export type WorkfrontOverviewDTO = DTOOf<IWorkfrontOverview, 'streams' | 'lastReferenceRefresh' | 'lastDiscovery'> & {
	streams: WorkfrontStreamDTO[]
	lastReferenceRefresh: WorkfrontRunDTO | null
	lastDiscovery: WorkfrontRunDTO | null
}

/** A queued piece of work: the run that follows it, and the bundle for a bundle sync. */
export interface WorkfrontAccepted {
	runId: string
	bundleId?: string
}

export interface BundleListParams {
	state?: WorkfrontBundleState
	syncStatus?: WorkfrontBundleSyncStatus
	hasOpenErrors?: boolean
	search?: string
	page?: number
	limit?: number
}

export interface RunListParams {
	process?: WorkfrontProcess
	status?: WorkfrontRunStatus
	page?: number
	limit?: number
}

export interface ErrorListParams {
	status?: WorkfrontSyncErrorStatus
	severity?: WorkfrontSyncErrorSeverity
	step?: WorkfrontSyncErrorStep
	page?: number
	limit?: number
}

/** `{ 'filters[name]': value }` for each set filter, plus paging. */
function listParams(filters: Record<string, string | number | boolean | undefined>, page?: number, limit?: number) {
	const params: Record<string, string | number> = {}
	for (const [name, value] of Object.entries(filters)) {
		if (value !== undefined && value !== '') params[`filters[${name}]`] = String(value)
	}
	if (page) params.page = page
	if (limit) params.limit = limit
	return params
}

/** The Workfront adapter's admin API: everything Admin › Workfront reads and asks for. */
export const workfrontApi = {
	health: (): Promise<WorkfrontHealthDTO> =>
		apiClient.get<WorkfrontHealthDTO>('/admin/workfront/health').then((r) => r.data),

	overview: (): Promise<WorkfrontOverviewDTO> =>
		apiClient.get<WorkfrontOverviewDTO>('/admin/workfront/overview').then((r) => r.data),

	listScope: (): Promise<WorkfrontScopeEntryDTO[]> =>
		apiClient.get<{ data: WorkfrontScopeEntryDTO[] }>('/admin/workfront/scope').then((r) => r.data.data),

	setScope: (key: WorkfrontScopeKey, value: unknown): Promise<WorkfrontScopeEntryDTO> =>
		apiClient
			.put<WorkfrontScopeEntryDTO>(`/admin/workfront/scope/${encodeURIComponent(key)}`, { value })
			.then((r) => r.data),

	listBundles: ({ page, limit, ...filters }: BundleListParams = {}): Promise<PaginatedResponse<WorkfrontBundleDTO>> =>
		apiClient
			.get<PaginatedResponse<WorkfrontBundleDTO>>('/admin/workfront/bundles', {
				params: listParams(filters, page, limit),
			})
			.then((r) => r.data),

	getBundle: (id: string): Promise<WorkfrontBundleDetailDTO> =>
		apiClient.get<WorkfrontBundleDetailDTO>(`/admin/workfront/bundles/${id}`).then((r) => r.data),

	refetchBundle: (id: string): Promise<WorkfrontAccepted> =>
		apiClient.post<WorkfrontAccepted>(`/admin/workfront/bundles/${id}/refetch`).then((r) => r.data),

	/** One bundle now, by its Workfront reference number. */
	syncBundle: (referenceNumber: number): Promise<WorkfrontAccepted> =>
		apiClient
			.post<WorkfrontAccepted>('/workfront/sync', { mode: 'bundle', reference: { referenceNumber } })
			.then((r) => r.data),

	runDiscovery: (): Promise<WorkfrontAccepted> =>
		apiClient.post<WorkfrontAccepted>('/workfront/sync', { mode: 'all' }).then((r) => r.data),

	refreshReference: (): Promise<WorkfrontAccepted> =>
		apiClient.post<WorkfrontAccepted>('/admin/workfront/reference/refresh').then((r) => r.data),

	listRuns: ({ page, limit, ...filters }: RunListParams = {}): Promise<PaginatedResponse<WorkfrontRunDTO>> =>
		apiClient
			.get<PaginatedResponse<WorkfrontRunDTO>>('/admin/workfront/runs', { params: listParams(filters, page, limit) })
			.then((r) => r.data),

	getRun: (id: string): Promise<WorkfrontRunDetailDTO> =>
		apiClient.get<WorkfrontRunDetailDTO>(`/admin/workfront/runs/${id}`).then((r) => r.data),

	listErrors: ({ page, limit, ...filters }: ErrorListParams = {}): Promise<PaginatedResponse<WorkfrontErrorDTO>> =>
		apiClient
			.get<PaginatedResponse<WorkfrontErrorDTO>>('/admin/workfront/errors', {
				params: listParams(filters, page, limit),
			})
			.then((r) => r.data),

	getError: (id: string): Promise<WorkfrontErrorDTO> =>
		apiClient.get<WorkfrontErrorDTO>(`/admin/workfront/errors/${id}`).then((r) => r.data),

	retryError: (id: string): Promise<WorkfrontErrorDTO> =>
		apiClient.post<WorkfrontErrorDTO>(`/admin/workfront/errors/${id}/retry`).then((r) => r.data),

	ignoreError: (id: string, reason: string): Promise<WorkfrontErrorDTO> =>
		apiClient.post<WorkfrontErrorDTO>(`/admin/workfront/errors/${id}/ignore`, { reason }).then((r) => r.data),
}
